import React, { useCallback, useState, forwardRef } from 'react';
import ContentSection from '@/pages/Products/VpsOciDetailPage/commons/ContentSection';

import * as Styles from './ServerManagement.styles';
import { IconArrow } from 'gatorcomponents';
import useLocale from '@/hooks/useLocale';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { vpsOciActions } from '@/redux/modules';
import { useParams } from 'react-router-dom';

import {
  ENABLE_VPS_OCI_SSH_KEY_SCREEN,
} from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import { billingAnalytics } from '@/analytics';

import {
  getIconBySlug,
  executeSlugAction,
} from '@/pages/Products/VpsOciDetailPage/utils';
import useServerStatus from '@/hooks/useServerStatus';
import RebuildModal from '@/pages/Products/VpsOciDetailPage/pages/ProductTemplatesPage/components/Rebuild/components/ModalRebuild';
import RebootModal from '@/pages/Products/VpsOciDetailPage/components/Reboot/RebootModal';

const REBOOT_MODAL_VIEW = {
  REBOOT: 'reboot',
  HISTORY: 'rebootHistory',
};

const ServerManagement = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [rebootModalView, setRebootModalView] = useState(null);

  const { onGoToSshKeysPage } = props;
  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: serverManagementLocale } = productDetailsLocale;
  const enableSshKeyScreen = useFeatureIsOn(ENABLE_VPS_OCI_SSH_KEY_SCREEN);
  const { hostingAndServers: hostingAndServersAnalytics } = billingAnalytics;

  const { id: hostingId } = useParams();

  const {
    management,
    productName,
    isRebooting,
    serverInfo,
    packageId,
  } = useSelector(
    state => ({
      management: state.vpsOci?.data?.management,
      productName: state.vpsOci?.data?.productName,
      isRebooting: state.vpsOci?.isRebooting,
      serverInfo: state.vpsOci?.data?.serverInfo,
      packageId: state.vpsOci?.data?.packageId,
    }),
    shallowEqual,
  );

  const {
    serverDisabledFeatures,
  } = useServerStatus({
    serverInfo,
    serverId: hostingId,
    serverPackageId: packageId,
  });

  /* SSH Keys */
  const handleSshKeysClick = useCallback((item) => {
    executeSlugAction(item.slug, {
      onGoToSshKeysPage,
    });
  }, [onGoToSshKeysPage]);

  /* Rebuild */
  const handleCloseModalRebuild = () => {
    setShowModal(false);
    hostingAndServersAnalytics.hostingRebuildModalCancelClick(productName);
  };

  const handleOpenModalRebuild = () => {
    setShowModal(true);
    hostingAndServersAnalytics.hostingRebuildConfirmClick(productName);
  };

  /* Reboot */
  const handleCloseRebootModal = () => {
    setRebootModalView(null);
  };

  const handleOpenRebootModal = () => {
    setRebootModalView(REBOOT_MODAL_VIEW.REBOOT);
  };

  const toggleModals = (modalToShow) => {
    setRebootModalView(modalToShow);
  };

  const handleRebootConfirm = useCallback(() => {
    if (!hostingId) return;

    dispatch(vpsOciActions.reboot.request(hostingId));

    hostingAndServersAnalytics.hostingMyPlanRebootClick(productName);
  }, [dispatch, hostingId, hostingAndServersAnalytics, productName]);


  let serverManagementList = [
    {
      slug: serverManagementLocale.serverManagement.items.reboot.slug,
      name: serverManagementLocale.serverManagement.items.reboot.name,
      action: handleOpenRebootModal,
      disabled: isRebooting,
    },
    {
      slug: serverManagementLocale.serverManagement.items.rebuild.slug,
      name: serverManagementLocale.serverManagement.items.rebuild.name,
      action: handleOpenModalRebuild,
      disabled: serverDisabledFeatures,
    },
    {
      slug: serverManagementLocale.serverManagement.items.sshKeys.slug,
      name: serverManagementLocale.serverManagement.items.sshKeys.name,
      action: handleSshKeysClick,
      disabled: serverDisabledFeatures,
    },
  ];

  const hasSshKeys = enableSshKeyScreen && management?.some(item => item.slug === serverManagementLocale.serverManagement.items.sshKeys.slug);

  if (!hasSshKeys) {
    serverManagementList = serverManagementList.filter(item => item.slug !== serverManagementLocale.serverManagement.items.sshKeys.slug);
  }


  return (
    <ContentSection
      ref={ref}
      direction="column"
      align="flex-start"
      justify="flex-start"
      title={serverManagementLocale.serverManagement.title.text}
      tooltip={serverManagementLocale.serverManagement.title.tooltip}
      testId="vps-serverManagement-section"
    >
      <Styles.ListItems>

        {serverManagementList
          .map((item) => {
            const iconItem = getIconBySlug(item.slug);
            const itemName = item.name;
            const isDisabledButton = item.disabled;

            return (
              <Styles.Item
                key={item.slug}
                onClick={!isDisabledButton ? () => item.action(item) : undefined}
                $isDisabledButton={isDisabledButton}
                data-testid={`vps-management-item-${item.slug}`}
              >
                {iconItem && <Styles.ItemIcon src={iconItem} alt={item.slug} data-testid={`vpsoci-management-icon-${item.slug}`} />}
                <Styles.ItemContent>
                  {itemName && <Styles.ItemName data-testid="vps-management-name">{itemName}</Styles.ItemName>}
                </Styles.ItemContent>
                {!isDisabledButton && (
                  <Styles.ItemArrow>
                    <IconArrow color="primaryMedium" size="md" direction="right" />
                  </Styles.ItemArrow>
                )}
              </Styles.Item>
            );
          })
        }
      </Styles.ListItems>

      {showModal && (
        <RebuildModal
          onClose={handleCloseModalRebuild}
        />
      )}

      {rebootModalView && (
        <RebootModal
          mode={rebootModalView}
          onClose={handleCloseRebootModal}
          onConfirm={handleRebootConfirm}
          onCheckRebootHistory={() => toggleModals(REBOOT_MODAL_VIEW.HISTORY)}
          onGoBack={() => toggleModals(REBOOT_MODAL_VIEW.REBOOT)}
        />
      )}
    </ContentSection>
  );
});

ServerManagement.displayName = 'ServerManagement';

export default ServerManagement;
